import React, { useState, useEffect, Component } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  TrendingUp, TrendingDown, DollarSign, Target, Shield,
  RefreshCw, X, Plus, Minus, ChevronUp, ChevronDown,
  BarChart3, Clock, AlertCircle, Loader2, ArrowLeft, AlertTriangle
} from 'lucide-react';
import FloatingLossIndicator from '../components/FloatingLossIndicator';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Error Boundary to catch TradingView widget errors silently
class ChartErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Silently catch TradingView widget errors
    console.log('Chart widget error (non-critical):', error.message);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="h-full w-full flex items-center justify-center bg-gray-50 rounded-lg">
          <div className="text-center text-gray-500">
            <BarChart3 className="w-12 h-12 mx-auto mb-2 text-gray-400" />
            <p>Chart loading...</p>
            <button 
              onClick={() => this.setState({ hasError: false })}
              className="mt-2 text-sm text-purple-600 hover:underline"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// TradingView Widget Component
const TradingViewChart = ({ symbol }) => {
  // Use direct iframe embedding for better isolation and reliability
  const getIframeSrc = (sym) => {
    const params = new URLSearchParams({
      autosize: 'true',
      symbol: sym,
      interval: '15',
      timezone: 'Etc/UTC',
      theme: 'dark',
      style: '1',
      locale: 'en',
      withdateranges: 'true',
      hide_side_toolbar: 'false',
      allow_symbol_change: 'false',
      details: 'false',
      hotlist: 'false',
      calendar: 'false',
    });
    return `https://s.tradingview.com/widgetembed/?frameElementId=tradingview_widget&${params.toString()}`;
  };

  return (
    <div 
      className="tradingview-widget-container h-full w-full"
      style={{ minHeight: '500px' }}
      data-testid="tradingview-chart"
    >
      <iframe
        title="TradingView Chart"
        src={getIframeSrc(symbol)}
        style={{ 
          width: '100%', 
          height: '100%', 
          border: 'none',
          minHeight: '500px'
        }}
        allowFullScreen
      />
    </div>
  );
};

// Position Row Component
const PositionRow = ({ position, onClose }) => {
  // Backend returns: position_id, symbol, side (long/short), volume, open_price, profit
  const isLong = position.side === 'long';
  const isProfit = position.profit >= 0;
  const displayType = isLong ? 'BUY' : 'SELL';

  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
      <div className="flex items-center gap-3">
        <Badge className={isLong ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}>
          {displayType}
        </Badge>
        <div>
          <p className="font-heading font-bold text-purple-900">{position.symbol}</p>
          <p className="text-xs text-gray-500">{position.volume} lots @ {position.open_price?.toFixed(5)}</p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="text-right">
          <p className={`font-mono font-bold ${isProfit ? 'text-green-600' : 'text-red-600'}`}>
            {isProfit ? '+' : ''}{position.profit?.toFixed(2)} USD
          </p>
          <p className="text-xs text-gray-500">
            Current: {position.current_price?.toFixed(5)}
          </p>
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => onClose(position.position_id)}
          className="text-gray-400 hover:text-red-500"
          data-testid={`close-position-${position.position_id}`}
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

const TradingPlatform = () => {
  const { challengeId } = useParams();
  const navigate = useNavigate();
  
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSymbol, setSelectedSymbol] = useState('FX:EURUSD');
  const [positions, setPositions] = useState([]);
  const [currentPrices, setCurrentPrices] = useState({});
  
  // Order form state
  const [orderType, setOrderType] = useState('buy');
  const [lotSize, setLotSize] = useState('0.01');
  const [stopLoss, setStopLoss] = useState('');
  const [takeProfit, setTakeProfit] = useState('');
  const [placingOrder, setPlacingOrder] = useState(false);

  const symbols = [
    { value: 'FX:EURUSD', label: 'EUR/USD', category: 'Forex' },
    { value: 'FX:GBPUSD', label: 'GBP/USD', category: 'Forex' },
    { value: 'FX:USDJPY', label: 'USD/JPY', category: 'Forex' },
    { value: 'TVC:GOLD', label: 'Gold', category: 'Commodities' },
    { value: 'BITSTAMP:BTCUSD', label: 'BTC/USD', category: 'Crypto' },
    { value: 'TVC:USOIL', label: 'Oil', category: 'Commodities' },
    { value: 'FOREXCOM:SPXUSD', label: 'S&P 500', category: 'Indices' },
    { value: 'BITSTAMP:ETHUSD', label: 'ETH/USD', category: 'Crypto' },
    { value: 'FX:USDCHF', label: 'USD/CHF', category: 'Forex' },
    { value: 'FX:AUDUSD', label: 'AUD/USD', category: 'Forex' },
    { value: 'FX:USDCAD', label: 'USD/CAD', category: 'Forex' },
    { value: 'TVC:SILVER', label: 'Silver', category: 'Commodities' },
    { value: 'FOREXCOM:NSXUSD', label: 'Nasdaq', category: 'Indices' },
  ];

  const fetchChallenge = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/challenges/${challengeId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setChallenge(data);
      } else {
        toast.error('Failed to load challenge');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error('Failed to load challenge');
    } finally {
      setLoading(false);
    }
  };

  const fetchPositions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/trading/positions/${challengeId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPositions(data.positions || []);
      }
    } catch (error) {
      console.error('Failed to fetch positions');
    }
  };

  useEffect(() => {
    fetchChallenge();
    fetchPositions();
    // Fetch prices from backend every 2 seconds
    updatePrices(); // Initial fetch
    const priceInterval = setInterval(updatePrices, 2000);
    return () => clearInterval(priceInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challengeId]);

  // Refetch price when symbol changes
  useEffect(() => {
    updatePrices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSymbol]);

  const [isLivePrice, setIsLivePrice] = useState(false);

  const updatePrices = async () => {
    // Fetch prices from backend (tries live data first, falls back to simulated)
    const symbolMap = {
      'FX:EURUSD': 'EURUSD',
      'FX:GBPUSD': 'GBPUSD',
      'FX:USDJPY': 'USDJPY',
      'FX:USDCHF': 'USDCHF',
      'FX:AUDUSD': 'AUDUSD',
      'FX:USDCAD': 'USDCAD',
      'BITSTAMP:BTCUSD': 'BTCUSD',
      'BITSTAMP:ETHUSD': 'ETHUSD',
      'TVC:GOLD': 'XAUUSD',
      'TVC:SILVER': 'XAGUSD',
      'TVC:USOIL': 'WTIUSD',
      'FOREXCOM:SPXUSD': 'US500',
      'FOREXCOM:NSXUSD': 'US100',
    };

    const newPrices = {};
    
    // Fetch price for currently selected symbol
    const backendSymbol = symbolMap[selectedSymbol];
    if (backendSymbol) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/trading/price/${backendSymbol}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (response.ok) {
          const data = await response.json();
          newPrices[selectedSymbol] = {
            bid: data.bid,
            ask: data.ask
          };
          setIsLivePrice(data.is_live || false);
        }
      } catch (error) {
        console.error('Failed to fetch price:', error);
        setIsLivePrice(false);
      }
    }
    
    // Fallback for symbols not fetched
    if (!newPrices[selectedSymbol]) {
      setIsLivePrice(false);
      const basePrices = {
        'FX:EURUSD': 1.0850,
        'FX:GBPUSD': 1.2650,
        'FX:USDJPY': 149.50,
        'FX:USDCHF': 0.8850,
        'FX:AUDUSD': 0.6550,
        'FX:USDCAD': 1.3550,
        'BITSTAMP:BTCUSD': 67500,
        'BITSTAMP:ETHUSD': 3550,
        'TVC:GOLD': 2650,
        'TVC:SILVER': 31.50,
        'TVC:USOIL': 72.50,
        'FOREXCOM:SPXUSD': 5950,
        'FOREXCOM:NSXUSD': 21000,
      };
      const base = basePrices[selectedSymbol] || 1;
      const spread = selectedSymbol.includes('BTC') ? 50 : selectedSymbol.includes('GOLD') ? 0.5 : 0.0003;
      newPrices[selectedSymbol] = {
        bid: base - spread/2,
        ask: base + spread/2
      };
    }
    
    setCurrentPrices(prev => ({...prev, ...newPrices}));
  };

  const placeOrder = async () => {
    if (!lotSize || parseFloat(lotSize) <= 0) {
      toast.error('Please enter a valid lot size');
      return;
    }

    setPlacingOrder(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Map TradingView symbols to broker symbols
      const symbolMapping = {
        'FX:EURUSD': 'EURUSD',
        'FX:GBPUSD': 'GBPUSD',
        'FX:USDJPY': 'USDJPY',
        'FX:USDCHF': 'USDCHF',
        'FX:AUDUSD': 'AUDUSD',
        'FX:USDCAD': 'USDCAD',
        'BITSTAMP:BTCUSD': 'BTCUSD',
        'BITSTAMP:ETHUSD': 'ETHUSD',
        'TVC:GOLD': 'XAUUSD',
        'TVC:SILVER': 'XAGUSD',
        'TVC:USOIL': 'WTIUSD',
        'FOREXCOM:SPXUSD': 'US500',
        'FOREXCOM:NSXUSD': 'US100',
      };
      const symbolClean = symbolMapping[selectedSymbol] || selectedSymbol.split(':')[1] || selectedSymbol;
      
      const response = await fetch(`${API_URL}/api/trading/order/${challengeId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          symbol: symbolClean,
          side: orderType,
          volume: parseFloat(lotSize),
          order_type: 'market',
          stop_loss: stopLoss ? parseFloat(stopLoss) : null,
          take_profit: takeProfit ? parseFloat(takeProfit) : null
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`${orderType.toUpperCase()} order placed successfully!`);
        
        // Refresh positions from backend
        fetchPositions();
        
        // Update challenge balance
        fetchChallenge();
        
        // Reset form
        setStopLoss('');
        setTakeProfit('');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to place order');
      }
    } catch (error) {
      toast.error('Failed to place order');
    } finally {
      setPlacingOrder(false);
    }
  };

  const closePosition = async (positionId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/trading/close/${challengeId}/${positionId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Position closed');
        // Refresh positions from backend
        fetchPositions();
        fetchChallenge();
      } else {
        toast.error('Failed to close position');
      }
    } catch (error) {
      toast.error('Failed to close position');
    }
  };

  const getCurrentPrice = () => {
    const price = currentPrices[selectedSymbol];
    if (!price) return { bid: 0, ask: 0, spread: 0 };
    return {
      bid: price.bid,
      ask: price.ask,
      spread: ((price.ask - price.bid) * 10000).toFixed(1)
    };
  };

  const price = getCurrentPrice();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16 bg-[hsl(220,20%,4%)]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(220,20%,4%)] pt-16" data-testid="trading-platform">
      {/* Top Bar */}
      <div className="bg-[hsl(220,20%,7%)] border-b border-white/10 px-4 py-3">
        <div className="max-w-[1800px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="text-gray-300 hover:text-white border-white/10 hover:bg-white/10 hover:border-white/20"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <div className="h-6 w-px bg-white/10" />
            <div>
              <p className="text-sm text-gray-500">Account</p>
              <p className="font-heading font-bold text-white">
                {challenge?.is_demo ? 'Demo Account' : challenge?.package_name}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-6">
            <div className="text-right">
              <p className="text-xs text-gray-500">Balance</p>
              <p className="font-heading font-bold text-lg text-white">
                ${challenge?.current_balance?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">Equity</p>
              <p className="font-heading font-bold text-lg text-green-400">
                ${challenge?.equity?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">P&L</p>
              <p className={`font-heading font-bold text-lg ${challenge?.profit_loss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {challenge?.profit_loss >= 0 ? '+' : ''}${challenge?.profit_loss?.toFixed(2)}
              </p>
            </div>
            {/* Floating Loss Indicator in Top Bar */}
            <div className="hidden md:block">
              <FloatingLossIndicator 
                floatingLossPercent={challenge?.floating_loss_percent || 0}
                maxFloatingLoss={challenge?.max_floating_loss || 2}
                size="small"
                showLabel={false}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Trading Area */}
      <div className="max-w-[1800px] mx-auto p-4">
        <div className="grid grid-cols-12 gap-4">
          {/* Chart Area */}
          <div className="col-span-12 lg:col-span-9">
            {/* Symbol Selector */}
            <Card className="bg-[hsl(220,20%,7%)] border border-white/10 mb-4">
              <CardContent className="p-3">
                <div className="flex items-center gap-4 overflow-x-auto">
                  {symbols.slice(0, 8).map((sym) => (
                    <button
                      key={sym.value}
                      onClick={() => setSelectedSymbol(sym.value)}
                      className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-all ${
                        selectedSymbol === sym.value
                          ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
                          : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      {sym.label}
                    </button>
                  ))}
                  <Select value={selectedSymbol} onValueChange={setSelectedSymbol}>
                    <SelectTrigger className="w-32 bg-white/5 border-white/10 text-gray-300">
                      <SelectValue placeholder="More..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[hsl(220,20%,10%)] border-white/10">
                      {symbols.map((sym) => (
                        <SelectItem key={sym.value} value={sym.value} className="text-gray-300 hover:text-white focus:text-white">
                          {sym.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* TradingView Chart */}
            <Card className="bg-[hsl(220,20%,7%)] border border-white/10 overflow-hidden" style={{ height: '500px' }}>
              <ChartErrorBoundary>
                <TradingViewChart symbol={selectedSymbol} />
              </ChartErrorBoundary>
            </Card>

            {/* Positions */}
            <Card className="bg-[hsl(220,20%,7%)] border border-white/10 mt-4">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-400" />
                  Open Positions ({positions.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {positions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Target className="w-12 h-12 mx-auto mb-3 text-gray-600" />
                    <p>No open positions</p>
                    <p className="text-sm">Place your first trade using the panel on the right</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {positions.map((position) => (
                      <PositionRow
                        key={position.position_id}
                        position={position}
                        onClose={closePosition}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Order Panel */}
          <div className="col-span-12 lg:col-span-3">
            <Card className="bg-[hsl(220,20%,7%)] border border-white/10 sticky top-20">
              <CardHeader className="pb-2 border-b border-white/10">
                <CardTitle className="font-heading text-lg text-white">
                  Place Order
                </CardTitle>
                <p className="text-sm text-gray-500">
                  {symbols.find(s => s.value === selectedSymbol)?.label}
                </p>
              </CardHeader>
              <CardContent className="pt-4">
                {/* Price Display */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <button
                    onClick={() => setOrderType('sell')}
                    className={`p-4 rounded-lg text-center transition-all ${
                      orderType === 'sell'
                        ? 'bg-red-500 text-white shadow-lg shadow-red-500/30'
                        : 'bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/30'
                    }`}
                  >
                    <p className="text-xs font-medium mb-1">SELL</p>
                    <p className="font-mono font-bold text-lg">
                      {price.bid.toFixed(selectedSymbol.includes('JPY') ? 3 : 5)}
                    </p>
                  </button>
                  <button
                    onClick={() => setOrderType('buy')}
                    className={`p-4 rounded-lg text-center transition-all ${
                      orderType === 'buy'
                        ? 'bg-green-500 text-white shadow-lg shadow-green-500/30'
                        : 'bg-green-500/10 text-green-400 hover:bg-green-500/20 border border-green-500/30'
                    }`}
                  >
                    <p className="text-xs font-medium mb-1">BUY</p>
                    <p className="font-mono font-bold text-lg">
                      {price.ask.toFixed(selectedSymbol.includes('JPY') ? 3 : 5)}
                    </p>
                  </button>
                </div>

                {/* Spread & Live indicator */}
                <div className="text-center mb-4 py-2 bg-white/5 rounded-lg flex items-center justify-center gap-3 border border-white/5">
                  <div>
                    <span className="text-xs text-gray-500">Spread: </span>
                    <span className="font-mono font-medium text-white">{price.spread} pips</span>
                  </div>
                  {isLivePrice ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500/20 text-green-400 text-xs font-medium rounded-full border border-green-500/30">
                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></span>
                      LIVE
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs font-medium rounded-full border border-yellow-500/30">
                      DEMO
                    </span>
                  )}
                </div>

                {/* Lot Size */}
                <div className="mb-4">
                  <Label className="text-gray-300 font-medium text-sm">Lot Size</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setLotSize(prev => Math.max(0.01, parseFloat(prev) - 0.01).toFixed(2))}
                      className="border-white/10 text-white hover:bg-white/10"
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <Input
                      type="number"
                      value={lotSize}
                      onChange={(e) => setLotSize(e.target.value)}
                      step="0.01"
                      min="0.01"
                      className="text-center font-mono bg-white/5 border-white/10 text-white"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setLotSize(prev => (parseFloat(prev) + 0.01).toFixed(2))}
                      className="border-white/10 text-white hover:bg-white/10"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex gap-2 mt-2">
                    {['0.01', '0.05', '0.1', '0.5', '1.0'].map((size) => (
                      <button
                        key={size}
                        onClick={() => setLotSize(size)}
                        className={`flex-1 py-1 text-xs rounded transition-all ${
                          lotSize === size
                            ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
                            : 'bg-white/5 text-gray-400 hover:bg-white/10'
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Stop Loss & Take Profit */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <Label className="text-gray-300 font-medium text-sm">Stop Loss</Label>
                    <Input
                      type="number"
                      value={stopLoss}
                      onChange={(e) => setStopLoss(e.target.value)}
                      placeholder="Optional"
                      step="0.00001"
                      className="mt-1 bg-white/5 border-white/10 text-white text-sm placeholder:text-gray-500"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300 font-medium text-sm">Take Profit</Label>
                    <Input
                      type="number"
                      value={takeProfit}
                      onChange={(e) => setTakeProfit(e.target.value)}
                      placeholder="Optional"
                      step="0.00001"
                      className="mt-1 bg-white/5 border-white/10 text-white text-sm placeholder:text-gray-500"
                    />
                  </div>
                </div>

                {/* Place Order Button */}
                <Button
                  onClick={placeOrder}
                  disabled={placingOrder}
                  data-testid="place-order-btn"
                  className={`w-full font-heading uppercase tracking-wider text-white rounded-lg h-12 shadow-lg transition-all ${
                    orderType === 'buy'
                      ? 'bg-gradient-to-r from-green-500 to-emerald-400 hover:from-green-600 hover:to-emerald-500 shadow-green-500/25'
                      : 'bg-gradient-to-r from-red-500 to-rose-400 hover:from-red-600 hover:to-rose-500 shadow-red-500/25'
                  }`}
                >
                  {placingOrder ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      {orderType === 'buy' ? 'BUY' : 'SELL'} {lotSize} LOTS
                    </>
                  )}
                </Button>

                {/* Risk Warning */}
                <div className="mt-4 p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                  <div className="flex gap-2">
                    <AlertCircle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-yellow-300">
                      Trading involves risk. Make sure to use proper risk management.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Account Summary */}
            <Card className="bg-[hsl(220,20%,7%)] border border-white/10 mt-4">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-sm text-white flex items-center gap-2">
                  <Shield className="w-4 h-4 text-blue-400" />
                  Account Rules
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                {/* Floating Loss Indicator - Full Size */}
                <div className="mb-3">
                  <FloatingLossIndicator 
                    floatingLossPercent={challenge?.floating_loss_percent || 0}
                    maxFloatingLoss={challenge?.max_floating_loss || 2}
                    size="default"
                  />
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Profit Target</span>
                  <span className="font-medium text-white">
                    {challenge?.profit_target_phase1 || 8}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Max Daily Loss</span>
                  <span className="font-medium text-white">
                    {challenge?.max_daily_loss || 5}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Max Total Loss</span>
                  <span className="font-medium text-white">
                    {challenge?.max_total_loss || 10}%
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradingPlatform;
